import axios from "axios";
import { CalendarEvent } from "@/api/calendarTypes";

export interface UpdateCalendarEventParams {
  accessToken: string;
  eventId: string;
  subject: string;
  startDateTime: string; // ISO datetime string
  endDateTime: string; // ISO datetime string
  timeZone: string;
  location?: string;
  body?: string;
  categories?: string[];
  bodyContentType?: "text" | "html";
  attendees?: Array<{
    emailAddress: {
      address: string;
      name?: string;
    };
    type?: "required" | "optional" | "resource";
  }>;
  isOnlineMeeting?: boolean;
  onlineMeetingProvider?:
    | "teamsForBusiness"
    | "skypeForBusiness"
    | "skypeForConsumer";
  isReminderOn?: boolean;
  reminderMinutesBeforeStart?: number;
}

/**
 * Updates an existing calendar event in the user's default calendar.
 *
 * @param params - Parameters for updating the calendar event
 * @returns The updated calendar event
 */
export async function updateCalendarEvent(
  params: UpdateCalendarEventParams
): Promise<CalendarEvent> {
  const {
    accessToken,
    eventId,
    subject,
    startDateTime,
    endDateTime,
    timeZone,
    location,
    body,
    categories,
    bodyContentType = "html",
    attendees,
    isOnlineMeeting,
    onlineMeetingProvider,
  } = params;

  if (!accessToken) {
    throw new Error(
      "Access token is missing. Please ensure user is authenticated."
    );
  }

  if (!eventId) {
    throw new Error("Event ID is required to update an event.");
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const eventData: Record<string, unknown> = {
      subject,
      start: {
        dateTime: startDateTime,
        timeZone: timeZone,
      },
      end: {
        dateTime: endDateTime,
        timeZone: timeZone,
      },
    };

    // Add optional fields only if provided
    if (location) {
      eventData.location = {
        displayName: location,
      };
    }

    if (body) {
      eventData.body = {
        contentType: bodyContentType === "html" ? "HTML" : "Text",
        content: body,
      };
    }

    if (attendees !== undefined) {
      eventData.attendees = attendees;
    }

    if (isOnlineMeeting !== undefined) {
      eventData.isOnlineMeeting = isOnlineMeeting;
    }

    eventData.categories = categories || [];

    if (onlineMeetingProvider) {
      eventData.onlineMeetingProvider = onlineMeetingProvider;
    }

    if (params.isReminderOn !== undefined) {
      eventData.isReminderOn = params.isReminderOn;
    }

    if (params.reminderMinutesBeforeStart !== undefined) {
      eventData.reminderMinutesBeforeStart = params.reminderMinutesBeforeStart;
    }

    // Update the event using PATCH
    const updateUrl = `https://graph.microsoft.com/v1.0/me/events/${eventId}`;
    const response = await axios.patch(updateUrl, eventData, { headers });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Error updating calendar event:",
        error.response?.data || error.message
      );
      throw new Error(
        `Graph API error: ${error.response?.status} ${
          error.response?.statusText || ""
        }`
      );
    } else {
      console.error("Unknown error:", error);
      throw error;
    }
  }
}
